import { useTranslation } from "react-i18next"
import { Formik, Form, Field, useField } from "formik"
import _ from 'lodash'
import { useState } from 'react'

import { useNavigate, useParams, Link } from "../navigation";
import Modal from "../components/Modal"
import { useDesigns } from "./DesignsProvider"
import { useDesignFamily } from "./DesignFamily"
import { useDesignRegistration } from "./DesignRegistration"
import { DesignRegistration } from "./designs"
import { designRegistrationUrl } from "./utils"
import { designCountries } from "../data"
import { DatePicker } from "../components/input/DatePicker"
import { useAgents } from "../agents/AgentsProvider"
import { AddAgentMask } from "../agents/AddAgent"
import { AsciiField } from "src/components/input/AsciiField";

type DesignRegistrationEditProps = DesignRegistration & {
    designer?: number | string,
    owner?: number | string,
    contact?: number | string
}

function asNumber(value: string | number) {
    return typeof value === 'string' ? parseInt(value) : value
}

function AddAgentButton({name, title, withCompanies = false}: {name: string, title: string, withCompanies?: boolean}) {
    const [showMask, setShowMask] = useState(false)

    const [, , helpers] = useField(name)
    const { setValue } = helpers

    return <>
        <button type="button" className="btn-secondary text-xs" onClick={() => setShowMask(true)}>{title}</button>
        {showMask && <AddAgentMask {...{
            title,
            setShowMask,
            withCompanies,
            update: (v) => setValue(v)
        }} />}
    </>
}

export default function PostDesignRegistration() {
    const { t } = useTranslation()
    const { reference } = useParams()
    const navigate = useNavigate()
    const {
        registrations,
        registrationByReference,
        postRegistration,
        postDesignAgentLink,
        deleteDesignAgentLink,
        designersByRegistrationId,
        ownersByRegistrationId,
        contactsByRegistrationId,
    } = useDesigns()
    const family = useDesignFamily()
    const { registration: contextRegistration } = useDesignRegistration()
    const { agents } = useAgents()

    // When editing, use registration from context. When adding, both will be undefined.
    const registration = contextRegistration ?? registrationByReference[reference]
    const isAdding = !registration

    const familyId = family?.designFamilyId

    const [companies, persons] = _.partition(agents, a => a.agentType === 'company')

    const otherRegistrationReferences = new Set(_(registrations).filter(r => r.registrationId !== registration?.registrationId).map(r => r.reference).value())

    const statusOptions: Array<{value: string, label: string}> = [
        { value: 'in-preparation', label: t('in-preparation') },
        { value: 'pending', label: t('pending') },
        { value: 'registered', label: t('registered') },
        { value: 'stopped', label: t('stopped') },
    ]

    const designTypeOptions: Array<{value: string, label: string}> = [
        { value: '3D', label: t('design-type-3d') },
        { value: '2D', label: t('design-type-2d') },
        { value: 'combined-2d-3d', label: t('design-type-combined') },
        { value: 'partial', label: t('design-type-partial') },
        { value: 'gui', label: t('design-type-gui') },
    ]

    const initialValues: DesignRegistrationEditProps = {
        ...(registration ?? {
            reference: '',
            externalReference: '',
            countryCode: 'WO',
            title: '',
            designType: undefined,
            status: 'in-preparation',
            applicationNumber: '',
            applicationDate: '',
            registrationNumber: '',
            registrationDate: '',
            publicationDate: '',
            renewalDate: '',
            expiryDate: '',
            abandonmentDate: '',
            officeUrl: '',
            familyId,
        }),
        designer: registration ? designersByRegistrationId[registration.registrationId]?.[0]?.agentId : undefined,
        owner: registration ? ownersByRegistrationId[registration.registrationId]?.[0]?.agentId : undefined,
        contact: registration ? contactsByRegistrationId[registration.registrationId]?.[0]?.agentId : undefined,
    }

    async function submitAgentLinks(registrationId: number, values: DesignRegistrationEditProps, role: 'designer' | 'owner' | 'contact') {
        const currentAgentId = role === 'designer' ? designersByRegistrationId[registrationId]?.[0]?.agentId :
                               role === 'owner' ? ownersByRegistrationId[registrationId]?.[0]?.agentId :
                               contactsByRegistrationId[registrationId]?.[0]?.agentId

        if (values[role] !== currentAgentId) {
            if (currentAgentId) {
                await deleteDesignAgentLink({ agentId: currentAgentId, registrationId, linkType: role })
            }
            if (values[role]) {
                await postDesignAgentLink({ agentId: asNumber(values[role]), registrationId, linkType: role })
            }
        }
    }

    return (
        <Modal>
            <Formik
                initialValues={initialValues}
                onSubmit={async (values: DesignRegistrationEditProps) => {
                    // Convert empty string dates to undefined for backend compatibility
                    const cleanedValues = {
                        ...values,
                        externalReference: values.externalReference || undefined,
                        title: values.title || undefined,
                        designType: values.designType || undefined,
                        applicationNumber: values.applicationNumber || undefined,
                        applicationDate: values.applicationDate || undefined,
                        registrationDate: values.registrationDate || undefined,
                        publicationDate: values.publicationDate || undefined,
                        renewalDate: values.renewalDate || undefined,
                        expiryDate: values.expiryDate || undefined,
                        abandonmentDate: values.abandonmentDate || undefined,
                        officeUrl: values.officeUrl || undefined,
                    }
                    const result = await postRegistration(cleanedValues)
                    if (!result?.registrationId) {
                        // Registration failed, don't proceed
                        return
                    }
                    await submitAgentLinks(result.registrationId, values, 'designer')
                    await submitAgentLinks(result.registrationId, values, 'owner')
                    await submitAgentLinks(result.registrationId, values, 'contact')
                    navigate(designRegistrationUrl(result))
                }}
                validate={(values: DesignRegistration) => {
                    const errors: Record<string, string> = {}
                    if (!values.reference) {
                        errors.reference = t('required')
                    } else if (otherRegistrationReferences.has(values.reference)) {
                        errors.reference = t('reference-used')
                    }
                    if (!values.countryCode) {
                        errors.countryCode = t('required')
                    }
                    if (!values.status) {
                        errors.status = t('required')
                    }
                    return errors
                }}
            >{({ touched, errors }) =>
                <Form>
                    <div className="p-4 flex flex-col gap-4">
                        <h2>{isAdding ? t('add-to-family-name', { name: family.reference }) : t('edit-design-registration', { name: initialValues.reference })}</h2>
                        {/* Row 1: Title (full width) */}
                        <label className="w-full">
                            <div className="label">{t('design-title')}</div>
                            <AsciiField name="title" className="form-input w-full mt-1" type="text" />
                        </label>
                        {/* Row 2: Country + Reference + External Reference */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="w-full">
                                <div className="label">{t('country')}</div>
                                <Field name="countryCode" className="form-select w-full mt-1" as="select" required>
                                    {designCountries.map(code => <option key={code} value={code}>{code}</option>)}
                                </Field>
                                {touched.countryCode && errors.countryCode && <div className="text-red-500 mt-1">{errors.countryCode}</div>}
                            </label>
                            <label className="w-full">
                                <div className="label">{t('reference')}</div>
                                <Field name="reference" className="form-input w-full mt-1" type="text"
                                    required autoFocus pattern="[a-zA-Z0-9 ._\-]*" title={t('use-usernames')}
                                />
                                {touched.reference && errors.reference && <div className="text-red-500 mt-1">{errors.reference}</div>}
                            </label>
                            <label className="w-full">
                                <div className="label">{t('externalReference')}</div>
                                <Field name="externalReference" className="form-input w-full mt-1" type="text" />
                            </label>
                        </div>
                        {/* Row 3: Status + Design Type */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="w-full">
                                <div className="label">{t('status')}</div>
                                <Field name="status" className="form-select w-full mt-1" as="select" required>
                                    {statusOptions.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                                </Field>
                                {touched.status && errors.status && <div className="text-red-500 mt-1">{errors.status}</div>}
                            </label>
                            <label className="w-full">
                                <div className="label">{t('designType')}</div>
                                <Field name="designType" className="form-select w-full mt-1" as="select">
                                    <option value="">{t('none')}</option>
                                    {designTypeOptions.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                                </Field>
                            </label>
                        </div>
                        {/* Row 5: Application Number + Application Date */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="w-full">
                                <div className="label">{t('applicationNumber')}</div>
                                <AsciiField name="applicationNumber" className="form-input w-full mt-1" type="text" />
                            </label>
                            <label className="w-full">
                                <div className="label">{t('application-date')}</div>
                                <Field name="applicationDate" className="form-input w-full mt-1 py-2" as={DatePicker} />
                            </label>
                        </div>
                        {/* Row 6: Registration Number + Registration Date */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="w-full">
                                <div className="label">{t('registration-number')}</div>
                                <AsciiField name="registrationNumber" className="form-input w-full mt-1" type="text" />
                            </label>
                            <label className="w-full">
                                <div className="label">{t('registration-date')}</div>
                                <Field name="registrationDate" className="form-input w-full mt-1 py-2" as={DatePicker} />
                            </label>
                        </div>
                        {/* Row 7: Publication Date + Renewal Date */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="w-full">
                                <div className="label">{t('publication-date')}</div>
                                <Field name="publicationDate" className="form-input w-full mt-1 py-2" as={DatePicker} />
                            </label>
                            <label className="w-full">
                                <div className="label">{t('renewal-date')}</div>
                                <Field name="renewalDate" className="form-input w-full mt-1 py-2" as={DatePicker} />
                            </label>
                        </div>
                        {/* Row 8: Expiry Date + Abandonment Date */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="w-full">
                                <div className="label">{t('expiry-date')}</div>
                                <Field name="expiryDate" className="form-input w-full mt-1 py-2" as={DatePicker} />
                            </label>
                            <label className="w-full">
                                <div className="label">{t('abandonmentDate')}</div>
                                <Field name="abandonmentDate" className="form-input w-full mt-1 py-2" as={DatePicker} />
                            </label>
                        </div>
                        {/* Row 9: External Link */}
                        <label className="w-full">
                            <div className="label">{t('external-link')}</div>
                            <Field name="officeUrl" className="form-input w-full mt-1" type="url" />
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className='w-full'>
                                <div className="mb-1 flex flex-row justify-between">
                                    <label htmlFor='designer' className='label'>{t('designer')}</label>
                                    <AddAgentButton name="designer" title={t('add-designer')} />
                                </div>
                                <Field id='designer' name="designer" className="form-select w-full" as="select">
                                    <option value={undefined}>{t('none')}</option>
                                    <optgroup label={t('persons')}>
                                        {persons.map(a => <option key={a.agentId} value={a.agentId}>{a.firstName} {a.lastName}</option>)}
                                    </optgroup>
                                </Field>
                            </div>
                            <div className='w-full'>
                                <div className="mb-1 flex flex-row justify-between">
                                    <label htmlFor='owner' className='label'>{t('owner')}</label>
                                    <AddAgentButton name="owner" title={t('add-owner')} withCompanies />
                                </div>
                                <Field id='owner' name="owner" className="form-select w-full" as="select">
                                    <option value={undefined}>{t('none')}</option>
                                    <optgroup label={t('companies')}>
                                        {companies.map(a => <option key={a.agentId} value={a.agentId}>{a.name}</option>)}
                                    </optgroup>
                                    <optgroup label={t('persons')}>
                                        {persons.map(a => <option key={a.agentId} value={a.agentId}>{a.firstName} {a.lastName}</option>)}
                                    </optgroup>
                                </Field>
                            </div>
                            <div className='w-full'>
                                <div className="mb-1 flex flex-row justify-between">
                                    <label htmlFor='contact' className='label'>{t('contact-person')}</label>
                                    <AddAgentButton name="contact" title={t('add-contact-person')} />
                                </div>
                                <Field id='contact' name="contact" className="form-select w-full" as="select">
                                    <option value={undefined}>{t('none')}</option>
                                    <optgroup label={t('persons')}>
                                        {persons.map(a => <option key={a.agentId} value={a.agentId}>{a.firstName} {a.lastName}</option>)}
                                    </optgroup>
                                </Field>
                            </div>
                        </div>
                    </div>
                    <div className="bg-pcx-200 flex flex-row-reverse gap-4 p-4">
                        <input type="submit" className="btn-primary" value={t('save')} />
                        <Link to={-1} className="btn-secondary">{t('cancel')}</Link>
                    </div>
                </Form>
                }</Formik>
        </Modal>
    )
}
